import React, { useState, useEffect } from 'react';
import {
  CubeIcon,
  EyeIcon,
  ArrowRightIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// Services
import { bridgeAPI, metadataAPI } from '../services/api';
import GuestConnectPrompt from './GuestConnectPrompt';
import SimulationStatusBanner from './SimulationStatusBanner';

const AssetsView = ({ userAddress }) => {
  const [assets, setAssets] = useState({
    galaxia: [],
    external: [],
    locked: [],
    wrapped: []
  });
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetFilter, setAssetFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userAddress) {
      loadUserAssets();
    } else {
      setLoading(false);
    }
  }, [userAddress]);

  const loadUserAssets = async () => {
    setLoading(true);
    try {
      const response = await bridgeAPI.getUserAssets(userAddress);

      // Mock asset data structure
      setAssets({
        galaxia: [
          {
            id: 'galaxia_1',
            name: 'Galaxia Avatar #123',
            type: 'avatar',
            metaverse: 'Galaxia',
            value: '150 GXAs',
            status: 'available',
            image: '/api/placeholder/150/150',
            attributes: [
              { trait_type: 'Rarity', value: 'Epic' },
              { trait_type: 'Universe', value: 'Cyber City' },
              { trait_type: 'Level', value: 15 }
            ]
          },
          {
            id: 'galaxia_2',
            name: 'Digital Sword',
            type: 'weapon',
            metaverse: 'Galaxia',
            value: '75 GXAs',
            status: 'available',
            image: '/api/placeholder/150/150',
            attributes: [
              { trait_type: 'Damage', value: 120 },
              { trait_type: 'Durability', value: '85%' }
            ]
          }
        ],
        external: [
          {
            id: 'external_1',
            name: 'Ready Player Me Avatar',
            type: 'avatar',
            metaverse: 'Ready Player Me',
            value: '200 GXAs',
            status: 'bridged',
            image: '/api/placeholder/150/150',
            attributes: [
              { trait_type: 'Body Type', value: 'Athletic' },
              { trait_type: 'Skin Tone', value: 'Medium' }
            ]
          }
        ],
        locked: [
          {
            id: 'locked_1',
            name: 'Locked Avatar #456',
            type: 'avatar',
            metaverse: 'Galaxia',
            value: '180 GXAs',
            status: 'locked',
            lockDate: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7), // 7 days ago
            targetMetaverse: 'OMA3',
            image: '/api/placeholder/150/150'
          }
        ],
        wrapped: [
          {
            id: 'wrapped_1',
            name: 'Wrapped OMA3 Asset',
            type: 'collectible',
            metaverse: 'OMA3',
            value: '95 GXAs',
            status: 'wrapped',
            originalMetaverse: 'OMA3',
            mintDate: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3), // 3 days ago
            image: '/api/placeholder/150/150'
          }
        ]
      });
    } catch (error) {
      console.error('Failed to load user assets:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAssets = () => {
    switch (assetFilter) {
      case 'galaxia':
        return assets.galaxia;
      case 'external':
        return assets.external;
      case 'locked':
        return assets.locked;
      case 'wrapped':
        return assets.wrapped;
      default:
        return [
          ...assets.galaxia,
          ...assets.external,
          ...assets.locked,
          ...assets.wrapped
        ];
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'available':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full galaxia-status-success">
            Available
          </span>
        );
      case 'locked':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full galaxia-status-warning">
            Locked
          </span>
        );
      case 'wrapped':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full galaxia-status-muted">
            Wrapped
          </span>
        );
      case 'bridged':
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full galaxia-status-muted">
            Bridged
          </span>
        );
      default:
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full galaxia-status-muted">
            Unknown
          </span>
        );
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;

    if (diff < 1000 * 60 * 60) return `${Math.floor(diff / (1000 * 60))} minutes ago`;
    if (diff < 1000 * 60 * 60 * 24) return `${Math.floor(diff / (1000 * 60 * 60))} hours ago`;
    return `${Math.floor(diff / (1000 * 60 * 60 * 24))} days ago`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="galaxia-card p-8 animate-pulse h-32" />
      </div>
    );
  }

  if (!userAddress) {
    return (
      <div className="space-y-6">
        <SimulationStatusBanner />
        <GuestConnectPrompt
          title="Explore your cross-metaverse assets"
          description="Connect a wallet to view locked, wrapped, and portable assets. You can still explore bridge flows in simulation mode without connecting."
          primaryTo="/bridge"
          primaryLabel="Explore Bridge"
        />
      </div>
    );
  }

  const filteredAssets = getFilteredAssets();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="galaxia-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-galaxia">My Assets</h1>
            <p className="text-galaxia-muted mt-1">
              Manage your assets across all connected metaverses
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-galaxia-muted">Total Assets</p>
            <p className="text-2xl font-bold text-galaxia">
              {filteredAssets.length}
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="galaxia-card p-6">
        <div className="flex items-center justify-between">
          <div className="flex space-x-4">
            <button
              onClick={() => setAssetFilter('all')}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                assetFilter === 'all'
                  ? 'bg-[rgba(206,147,216,0.15)] border border-[var(--galaxia-accent)]'
                  : 'border border-[var(--galaxia-border)]'
              }`}
            >
              All Assets ({assets.galaxia.length + assets.external.length + assets.locked.length + assets.wrapped.length})
            </button>
            <button
              onClick={() => setAssetFilter('galaxia')}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                assetFilter === 'galaxia'
                  ? 'bg-[rgba(206,147,216,0.15)] border border-[var(--galaxia-accent)]'
                  : 'border border-[var(--galaxia-border)]'
              }`}
            >
              Galaxia ({assets.galaxia.length})
            </button>
            <button
              onClick={() => setAssetFilter('external')}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                assetFilter === 'external'
                  ? 'bg-[rgba(206,147,216,0.15)] border border-[var(--galaxia-accent)]'
                  : 'border border-[var(--galaxia-border)]'
              }`}
            >
              External ({assets.external.length})
            </button>
            <button
              onClick={() => setAssetFilter('locked')}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                assetFilter === 'locked'
                  ? 'bg-[rgba(206,147,216,0.15)] border border-[var(--galaxia-accent)]'
                  : 'border border-[var(--galaxia-border)]'
              }`}
            >
              Locked ({assets.locked.length})
            </button>
            <button
              onClick={() => setAssetFilter('wrapped')}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                assetFilter === 'wrapped'
                  ? 'bg-[rgba(206,147,216,0.15)] border border-[var(--galaxia-accent)]'
                  : 'border border-[var(--galaxia-border)]'
              }`}
            >
              Wrapped ({assets.wrapped.length})
            </button>
          </div>
        </div>
      </div>

      {/* Assets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredAssets.map((asset) => (
          <div
            key={asset.id}
            className="galaxia-card overflow-hidden transition-shadow cursor-pointer"
            onClick={() => setSelectedAsset(asset)}
          >
            <div className="aspect-w-1 aspect-h-1">
              <img
                src={asset.image}
                alt={asset.name}
                className="w-full h-48 object-cover"
              />
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-galaxia truncate">
                    {asset.name}
                  </h3>
                  <p className="text-xs text-galaxia-muted mt-1">
                    {asset.metaverse}
                  </p>
                </div>
                {getStatusBadge(asset.status)}
              </div>

              <div className="mt-3">
                <p className="text-sm font-medium text-galaxia">
                  {asset.value}
                </p>
                {asset.type && (
                  <p className="text-xs text-galaxia-muted capitalize">
                    {asset.type}
                  </p>
                )}
              </div>

              {asset.lockDate && (
                <div className="mt-2 text-xs text-galaxia-muted">
                  Locked {formatTimeAgo(asset.lockDate)}
                  {asset.targetMetaverse && ` for ${asset.targetMetaverse}`}
                </div>
              )}

              {asset.mintDate && (
                <div className="mt-2 text-xs text-galaxia-muted">
                  Wrapped {formatTimeAgo(asset.mintDate)}
                  {asset.originalMetaverse && ` from ${asset.originalMetaverse}`}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredAssets.length === 0 && (
        <div className="text-center py-12">
          <CubeIcon className="mx-auto h-12 w-12 text-galaxia-muted" />
          <h3 className="mt-2 text-sm font-medium text-galaxia">No assets found</h3>
          <p className="mt-1 text-sm text-galaxia-muted">
            {assetFilter === 'all'
              ? 'You don\'t have any assets yet. Start by bridging some assets!'
              : `No assets found in the ${assetFilter} category.`
            }
          </p>
          {assetFilter !== 'all' && (
            <div className="mt-6">
              <button
                onClick={() => setAssetFilter('all')}
                className="galaxia-btn-primary inline-flex items-center px-4 py-2 text-sm font-medium"
              >
                View All Assets
              </button>
            </div>
          )}
        </div>
      )}

      {/* Asset Detail Modal */}
      {selectedAsset && (
        <div className="fixed inset-0 overflow-y-auto h-full w-full z-50" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="relative top-20 mx-auto p-5 w-full max-w-2xl galaxia-card shadow-lg" style={{ border: '1px solid var(--galaxia-border)' }}>
            <div className="flex items-start justify-between">
              <div className="flex items-center">
                <img
                  src={selectedAsset.image}
                  alt={selectedAsset.name}
                  className="w-16 h-16 object-cover rounded-lg mr-4"
                />
                <div>
                  <h3 className="text-lg font-medium text-galaxia">
                    {selectedAsset.name}
                  </h3>
                  <p className="text-sm text-galaxia-muted">
                    {selectedAsset.metaverse} • {selectedAsset.type}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedAsset(null)}
                className="text-galaxia-muted hover:text-galaxia-muted"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-galaxia mb-3">Asset Details</h4>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-sm text-galaxia-muted">Value</dt>
                      <dd className="text-sm font-medium text-galaxia">{selectedAsset.value}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-galaxia-muted">Status</dt>
                      <dd className="text-sm font-medium text-galaxia">{getStatusBadge(selectedAsset.status)}</dd>
                    </div>
                    {selectedAsset.lockDate && (
                      <div>
                        <dt className="text-sm text-galaxia-muted">Locked Date</dt>
                        <dd className="text-sm font-medium text-galaxia">
                          {selectedAsset.lockDate.toLocaleDateString()}
                        </dd>
                      </div>
                    )}
                    {selectedAsset.mintDate && (
                      <div>
                        <dt className="text-sm text-galaxia-muted">Mint Date</dt>
                        <dd className="text-sm font-medium text-galaxia">
                          {selectedAsset.mintDate.toLocaleDateString()}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-galaxia mb-3">Attributes</h4>
                  <div className="space-y-2">
                    {selectedAsset.attributes?.map((attr, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-galaxia-muted">{attr.trait_type}:</span>
                        <span className="font-medium text-galaxia">{attr.value}</span>
                      </div>
                    )) || (
                      <p className="text-sm text-galaxia-muted">No attributes available</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--galaxia-border)' }}>
                <div className="flex space-x-3">
                  {selectedAsset.status === 'available' && (
                    <button className="flex-1 galaxia-btn-primary px-4 py-2 text-sm font-medium">
                      Bridge to Metaverse
                    </button>
                  )}
                  {selectedAsset.status === 'locked' && (
                    <button className="flex-1 galaxia-btn-primary px-4 py-2 text-sm font-medium">
                      Unlock Asset
                    </button>
                  )}
                  {selectedAsset.status === 'wrapped' && (
                    <button className="flex-1 galaxia-btn-primary px-4 py-2 text-sm font-medium">
                      Return to Original
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedAsset(null)}
                    className="flex-1 galaxia-btn-secondary px-4 py-2 text-sm font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetsView;
